import {
  A4_HEIGHT_MM,
  A4_WIDTH_MM,
  CONTENT_TOP_MM,
  paginate,
} from "@/app/report/common/export/paginate"
import {
  collectPdfTargets,
  measureChunkHeightsMm,
  MeasureChunkOptions,
  chunkTypeOf,
} from "@/app/report/common/export/chunks"

/**
 * 미리보기에 출력물과 같은 페이지 경계를 만든다.
 *
 * 미리보기와 PDF 는 원래 같은 DOM 을 쓴다(미리보기를 그대로 캡처한다). 갈라지는 건 마지막
 * 페이지 배치 한 단계뿐이라, 그 배치 결과(paginate)를 미리보기에도 여백으로 반영하면
 * "보이는 대로 나온다" 가 성립한다.
 *
 * DOM 구조는 건드리지 않는다 — 덩어리를 페이지 박스 안으로 옮겨 담으면 React 가 리마운트하면서
 * 차트가 다시 그려지고, 그 과정에서 폭이 흔들리면 차트가 깨진다. 대신 각 덩어리의 margin-top
 * 만 조정해 제자리로 밀어낸다. margin 은 요소 박스 바깥이라 캡처 결과에도 영향이 없다.
 */

/** 페이지 사이 시각적 간격 (미리보기 전용) */
export const PREVIEW_PAGE_GAP_PX = 24

export type PreviewPageBox = {
  /** 1-based */
  page: number
  topPx: number
  heightPx: number
  /**
   * 미리보기 컨테이너는 콘텐츠 폭(190mm)이지만 페이지는 A4 전체 폭(210mm)이다.
   * 좌우 여백이 실제 문서처럼 보이도록 컨테이너 밖으로 내밀어 그린다(음수).
   */
  leftPx: number
  widthPx: number
}

const topWithin = (container: HTMLElement, el: HTMLElement) =>
  el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop

/** 이전에 넣은 페이지 여백을 걷어낸다. */
export const clearPreviewPagination = (container: HTMLElement | null | undefined) => {
  if (!container) {
    return
  }

  container.style.paddingTop = ""
  container.querySelectorAll<HTMLElement>("[data-preview-page-offset]").forEach((el) => {
    el.style.marginTop = ""
    el.removeAttribute("data-preview-page-offset")
  })
}

/**
 * 미리보기 덩어리들을 출력 페이지 위치로 밀어내고, 그릴 페이지 박스 목록을 돌려준다.
 * 차트가 다 그려진 뒤에 불러야 높이가 맞는다.
 */
export const applyPreviewPagination = (
  container: HTMLElement | null | undefined,
  contentWidthMm: number,
  options: MeasureChunkOptions = {},
): PreviewPageBox[] => {
  if (!container) {
    return []
  }

  clearPreviewPagination(container)

  const containerWidth = container.clientWidth
  if (!containerWidth) {
    return []
  }

  const targets = collectPdfTargets(container)
  if (targets.length === 0) {
    return []
  }

  const heights = measureChunkHeightsMm(targets, containerWidth, contentWidthMm, options)
  const { placements, totalPages } = paginate(heights, {
    types: targets.map(chunkTypeOf),
  })

  const pxPerMm = containerWidth / contentWidthMm
  const pageHeightPx = A4_HEIGHT_MM * pxPerMm
  const pageStridePx = pageHeightPx + PREVIEW_PAGE_GAP_PX

  const desiredTopPx = (page: number, yMm: number) => (page - 1) * pageStridePx + yMm * pxPerMm

  // 첫 덩어리는 margin 으로 못 민다 — 컨테이너의 첫 자식이라 margin 이 위로 상쇄되면서
  // 컨테이너만 내려가고 덩어리는 제자리다. 상단 여백은 컨테이너 padding 으로 준다.
  container.style.paddingTop = `${CONTENT_TOP_MM * pxPerMm}px`

  // margin 이 부모와 상쇄(margin collapsing)되면 한 번에 원하는 위치로 가지 않는다.
  // 실제로 움직였는지 확인하고 남은 차이만 마저 준다 — 안 움직였다면 더 줘봐야 소용없고
  // 값만 쌓이므로 되돌린다.
  const nudge = (el: HTMLElement, wanted: number) => {
    const before = topWithin(container, el)
    const delta = wanted - before

    if (Math.abs(delta) < 0.5) {
      return
    }

    const current = parseFloat(el.style.marginTop) || 0
    el.style.marginTop = `${current + delta}px`
    el.setAttribute("data-preview-page-offset", "")

    const after = topWithin(container, el)
    if (Math.abs(after - before) < 0.5) {
      // 전혀 움직이지 않았다 — 이 요소에는 margin 이 듣지 않는다. 원래대로 되돌린다.
      el.style.marginTop = current ? `${current}px` : ""

      return
    }

    // 상쇄로 일부만 움직였으면 남은 만큼 한 번 더.
    const remaining = wanted - after
    if (Math.abs(remaining) >= 0.5) {
      el.style.marginTop = `${(parseFloat(el.style.marginTop) || 0) + remaining}px`
    }
  }

  placements.forEach((placement, index) => {
    if (!placement.placed) {
      return
    }

    nudge(targets[index], desiredTopPx(placement.page, placement.y))
  })

  const pageWidthPx = A4_WIDTH_MM * pxPerMm

  return Array.from({ length: totalPages }, (_, i) => ({
    page: i + 1,
    topPx: i * pageStridePx,
    heightPx: pageHeightPx,
    leftPx: -(pageWidthPx - containerWidth) / 2,
    widthPx: pageWidthPx,
  }))
}

/**
 * 차트가 다 그려질 때까지 기다린다.
 *
 * 차트는 마운트 후 컨테이너 폭을 재서 비동기로 그리므로, 열자마자 재면 높이가 덜 자란 상태다.
 * 컨테이너 높이가 몇 번 연속 그대로면 안정된 것으로 본다.
 */
export const waitForStableLayout = async (container: HTMLElement, timeoutMs = 4000) => {
  const startedAt = Date.now()
  let lastHeight = -1
  let stableCount = 0

  while (Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 120))

    const height = container.scrollHeight
    if (height === lastHeight) {
      stableCount += 1
      if (stableCount >= 3) {
        return
      }
    } else {
      stableCount = 0
      lastHeight = height
    }
  }
}

/**
 * 미리보기 컨테이너가 준비될 때까지 기다린다.
 *
 * 다이얼로그는 페이드로 열려서, 여는 시점에는 아직 마운트 전이거나 폭이 0 일 수 있다.
 * 폭이 잡히고 배치 대상이 하나라도 생길 때까지 짧게 폴링한다.
 */
export const waitForPreviewContainer = async (
  selector: string,
  timeoutMs = 5000,
): Promise<HTMLElement | null> => {
  const startedAt = Date.now()

  while (Date.now() - startedAt < timeoutMs) {
    const container = document.querySelector(selector) as HTMLElement | null
    if (container && container.clientWidth > 0 && collectPdfTargets(container).length > 0) {
      return container
    }

    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  return null
}
